"use client";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";

import { ArrowLeft, ChevronLeft } from "lucide-react";
import { useAuth } from "@/core/auth/contexts/AuthContext";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import {
  addSubCategory,
  AddSubCategorySchema,
  AddSubCategorySchemaType,
} from "@/services/addSubCategory";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchCategories } from "@/services/getCategories";
import { getCompanies } from "@/services/getCompanies";

export default function AddSubCategory() {
  const { user } = useAuth();

  const addSubCategoryForm = useForm<AddSubCategorySchemaType>({
    resolver: zodResolver(AddSubCategorySchema),
    defaultValues: {
      name: "",
      description: "",
      category: "",
      company: user?.role === "super-admin" ? "" : (user?.company?._id || ""),
    },
  });
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const CategoryData = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const data = await fetchCategories();
      return data.map((category: any) => {
        return { ...category, company: category.company?.name };
      });
    },
    enabled: !!user?.company?._id || user?.role === "super-admin",
  });

  const CompaniesData = useQuery({
    queryKey: ["companies"],
    queryFn: async () => {
      const data = await getCompanies();
      return data;
    },
    enabled: user?.role === "super-admin",
  });

  const handleAddCategoryMutation = useMutation({
    mutationFn: async (formData: AddSubCategorySchemaType) => {
      const payload: any = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
      };
      
      if (user?.role === "super-admin") {
        payload.company = formData.company;
      } else {
        payload.company = user?.company?._id;
      }
      
      return addSubCategory(payload);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sub-categories"] });
      toast.success("Sub Category added successfully");
      navigate("/app/sub-categories");
    },
    onError: (error) => {
      const newError = error as any;
      console.error(
        "An error occurred while adding the sub category:",
        newError.response?.data?.message
      );
      toast.error(newError.response?.data?.message ?? "An error occured");
    },
  });

  const { isPending } = handleAddCategoryMutation;

  const onSubmit = (values: AddSubCategorySchemaType) => {
    handleAddCategoryMutation.mutate(values);
  };

  return (
    <div className="w-full p-6 max-w-7xl mx-auto space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-8">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Link
              to="/app/sub-categories"
              className={buttonVariants({ variant: "ghost", size: "sm" })}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
            </Link>
            <h1 className="text-2xl font-bold tracking-tight">
              Add Sub Category
            </h1>
          </div>
          <p className="text-muted-foreground">
            Create a new sub-category for your services
          </p>
        </div>
      </div>

      {/* Main Form Section */}
      <div className="mb-4 max-w-xl mx-auto">
        <Form {...addSubCategoryForm}>
          <form onSubmit={addSubCategoryForm.handleSubmit(onSubmit)}>
            <div className="space-y-4">
              {/* Sub Category Details Card */}
              <div className="bg-card border rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-6">
                  Sub Category Details
                </h2>
                <div className="grid gap-6">
                  <div className="w-full">
                    <FormField
                      control={addSubCategoryForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter sub-category name"
                              {...field}
                              className="max-w-md"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="w-full">
                    <FormField
                      control={addSubCategoryForm.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter description"
                              {...field}
                              className="max-w-md"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="w-full">
                    <FormField
                      control={addSubCategoryForm.control}
                      name="category"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Parent Category</FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger className="max-w-md">
                                  <SelectValue placeholder="Select a category" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {CategoryData.data?.map((category: any) => (
                                  <SelectItem
                                    key={category._id}
                                    value={category._id}
                                  >
                                    {category.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {user?.role === "super-admin" && (
                    <div className="w-full">
                      <FormField
                        control={addSubCategoryForm.control}
                        name="company"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company</FormLabel>
                            <FormControl>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger className="max-w-md">
                                    <SelectValue placeholder="Select a company" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {CompaniesData.data?.map((company: any) => (
                                    <SelectItem
                                      key={company._id}
                                      value={company._id}
                                    >
                                      {company.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  className="min-w-[150px]"
                  disabled={isPending}
                >
                  {isPending ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Adding...
                    </span>
                  ) : (
                    "Add Sub Category"
                  )}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
